import Button from 'Components/Base/Button'
import Card from 'Components/Base/Card'
import Checkbox from 'Components/Base/Checkbox'
import Column from 'Components/Base/Column'
import Row from 'Components/Base/Row'
import Typography from 'Components/Base/Typography'
import { StyledContainer } from 'Pages/AppointmentPage'
import { StyledErrorText } from 'Styles/global'
import { sizes } from 'Styles/sizes'
import { useFormik } from 'formik'
import React, { useState } from 'react'
import styled, { useTheme } from 'styled-components'
import * as Yup from 'yup'

interface Props {
  onBackPressed: () => void

  onSuccess: () => void

  loading?: boolean
}

export default function CTLProgramRequirements({ onBackPressed, onSuccess, loading }: Props) {
  const theme = useTheme()

  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  const validationSchema = Yup.object().shape({
    acceptedProgramRequirements: Yup.boolean().oneOf([true], 'Please accept the program requirements'),
  })

  const initialValues = {
    acceptedProgramRequirements: false,
  }

  const onSubmit = () => {
    onSuccess()
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  })

  return (
    <StyledContainer>
      <h1 className="id-header">Great to have you onboard!</h1>
      <Typography size={24} styles={{ marginBottom: 0, marginTop: 20 }}>
        Let's go over a few important details
      </Typography>
      <Typography size={24} styles={{ marginBottom: 20, marginTop: 20 }}>
        Please take a moment to review our program requirements and acknowledge that you understand what's expected.
      </Typography>

      <Card styles={{ width: '100%', borderRadius: 0, height: 393 }}>
        <Typography size={32} font="bold" styles={{ marginBottom: 20, marginTop: 20 }}>
          Program Requirements:
        </Typography>
        <Column style={{ textAlign: 'left', width: '80%', marginLeft: 'auto', marginRight: 'auto' }}>
          <Row styles={{ marginBottom: 15 }}>
            <Typography size={24} font="bold" styles={{ marginRight: 10 }}>
              1.
            </Typography>
            <Typography size={24}>
              <b>Regular Weigh-ins:</b> Weigh yourself at least monthly and share your results with your health coach to effectively track your
              progress.
            </Typography>
          </Row>
          <Row styles={{ marginBottom: 15 }}>
            <Typography size={24} font="bold" styles={{ marginRight: 10 }}>
              2.
            </Typography>
            <Typography size={24}>
              <b>Health Coach Check-ins:</b> Keep in regular contact with your health coach through phone consultations or messaging via the app.
            </Typography>
          </Row>
          <Row>
            <Typography size={24} font="bold" styles={{ marginRight: 10 }}>
              3.
            </Typography>
            <Typography size={24}>
              <b>Food Tracking:</b> Keep a detailed log of your food intake to help monitor your nutrition.
            </Typography>
          </Row>
        </Column>
      </Card>
      <Typography size={24} styles={{ marginBottom: 20, marginTop: 20 }}>
        Tracking your progress is crucial for success, and the Tria Health app makes it super easy. With automatic weight tracking and a simple way to
        connect with your coach, staying on top of your goals is a breeze.
      </Typography>

      <StyledErrorText size={18}>By clicking "I Understand," you acknowledge that you have read and agree to these requirements.</StyledErrorText>

      <StyledCheckboxRow
        onClick={(e) => formik.setFieldValue('acceptedProgramRequirements', !formik.values.acceptedProgramRequirements)}
        style={{ margin: '40px auto 40px auto' }}
      >
        <Checkbox
          checked={formik.values.acceptedProgramRequirements}
          styles={{
            marginRight: 20,
            borderRadius: 0,
            backgroundColor: theme.colors.white,
            border: '1px solid black',
          }}
          checkStyles={{ backgroundColor: theme.colors.black }}
        />
        <StyledCheckBoxRow>
          <Typography styles={{ fontWeight: 'bold', marginRight: 10 }} size={18}>
            I Understand
          </Typography>
        </StyledCheckBoxRow>
      </StyledCheckboxRow>
      {!formik.isValid && attemptedSubmit && (
        <Row
          styles={{
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
          center
        >
          <StyledErrorText>Please accept all policies and sign</StyledErrorText>
        </Row>
      )}
      <StyledButtonContainer>
        <StyledButton pageName="appointment_consent" text="Back" styles={{ backgroundColor: theme.colors.darkGray }} onClick={onBackPressed} />
        <div style={{ minWidth: '5%', minHeight: '20px' }}></div>
        <StyledButton
          pageName="appointment_consent"
          text="Next"
          disabled={!formik.values.acceptedProgramRequirements}
          loading={loading}
          onClick={() => {
            formik.handleSubmit()
            setAttemptedSubmit(true)
          }}
        />
      </StyledButtonContainer>
    </StyledContainer>
  )
}

const StyledCheckboxRow = styled.div`
  align-content: center;
  margin-top: 1px;
  margin-bottom: 1px;
  display: flex;
  justify-content: center;
  @media ${sizes.tablet} {
    justify-content: left;
    align-content: left;
  }
`
const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 50px;
  flex-direction: column;
  @media ${sizes.tablet} {
    flex-direction: row;
  }
`

const StyledCheckBoxRow = styled.div`
  display: flex;
  text-align: left;
  cursor: pointer;
`
const StyledButton = styled(Button)`
  width: 100%;
  height: 40px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.primary1};
  @media ${sizes.tablet} {
    width: 25%;
  }
`
